import React, { useState, useEffect } from "react";
import { Menu, Dropdown, Badge, Avatar, List, Button, Spin } from "antd";
import {
  MailOutlined,
  BellOutlined,
  WarningOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import Flex from "components/shared-components/Flex";
import { DeblurSharp } from "@mui/icons-material";
import { APP_PREFIX_PATH, WriteServices } from "configs/AppConfig";
import { useHistory } from "react-router-dom";
import LearningService from "services/LearningService";

// ✅ Get correct icon for each notification type
const getIcon = (icon) => {
  switch (icon) {
    case "mail":
      return <MailOutlined />;
    case "alert":
      return <WarningOutlined />;
    case "check":
      return <CheckCircleOutlined />;
    default:
      return <MailOutlined />;
  }
};

const styles = {
  listItem: {
    padding: "8px 12px",
    transition: "background-color 0.2s ease",
  },
  content: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "anchor-center",
    width: "100%",
  },
  left: {
    display: "flex",
    alignItems: "anchor-center",
    gap: "10px",
    flex: 1,
  },
  text: {
    display: "flex",
    flexDirection: "column",
    lineHeight: 1.3,
    wordBreak: "break-word",
    color: "#333",
    // maxWidth: "200px",
  },
  message: {
    fontWeight: 500,
    color: "#1f1f1f",
  },
  desc: {
    color: "#888",
    fontSize: "12px",
  },
  date: {
    fontSize: "12px",
    color: "#777",
    whiteSpace: "nowrap",
    marginLeft: "10px",
  },
};

// ✅ Build list layout for notifications
const getNotificationBody = (list, onMarkRead) => {
  if (list.length === 0) {
    return (
      <div className="empty-notification">
        <img
          src="https://gw.alipayobjects.com/zos/rmsportal/sAuJeJzSKbUmHfBQRzmZ.svg"
          alt="empty"
        />
        <p className="mt-3">You have viewed all notifications</p>
      </div>
    );
  }

  return (
    <List
      size="small"
      itemLayout="horizontal"
      dataSource={list}
      renderItem={(item) => (
        <List.Item
          className="list-clickable"
          onClick={() => onMarkRead(item.NotificationsID)}
          style={{
            ...styles.listItem,
            backgroundColor: item.IsRead ? "#f9f9f9" : "#e6f7ff",
          }}
        >
          <div style={styles.content}>
            <div style={styles.left}>
              <Avatar
                src={item.img ? `/img/avatars/${item.img}` : undefined}
                icon={!item.img && getIcon(item.icon)}
              />
              <div style={styles.text}>
                <span style={styles.message}>{item.Text}</span>
                {item.desc && <span style={styles.desc}>{item.desc}</span>}
              </div>
            </div>
            <small style={styles.date}>{item.RequestDate}</small>
          </div>
        </List.Item>
      )}
    />
  );
};

export const NavNotification = () => {
  const [visible, setVisible] = useState(false);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);

  const history = useHistory();

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    setLoading(true);
    try {
      // Directly call your service (it already does the axios call)
      const result = await LearningService.GetLMSRequestNotifications();

      if (!result) throw new Error("No notifications found");

      console.log("formatted", result);
      const formatted = Array.isArray(result.Records)
        ? result.Records.map((item, idx) => ({
            id: item.NotificationsID || idx,
            ...item,
            IsRead: false,
          }))
        : [];
      setData(formatted);
      console.log("setformatted", formatted);
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleVisibleChange = (flag) => {
    setVisible(flag);
    console.log("read2");
  };

  // ✅ Mark a notification as read when clicked
  const handleMarkRead = (id) => {
    const readRequest = {
      NotificationsID: id,
      IsClear: false,
      IsRead: true,
    };
    console.log(readRequest);
    const readNotifications = LearningService.UpdateRequestNotifications;
    Promise.resolve(readNotifications(readRequest)).then((res) => {
      if (res === null) {
        return;
      }
      if (res?.data?.Status == 1) {
        // Optionally update state or handle success
        fetchNotifications();
      }
    });
    setData((prev) =>
      prev.map((item) => (item.id === id ? { ...item, isRead: true } : item))
    );
    console.log("read");
    history.push(`${APP_PREFIX_PATH}/taskbox`);
  };

  // ✅ Mark all as read
  const handleMarkAllRead = () => {
    data.forEach((item) => {
      const readAllRequest = {
        NotificationsID: item.NotificationsID,
        IsClear: false,
        IsRead: true,
      };
      console.log(readAllRequest);
      const readAllNotifications = LearningService.UpdateRequestNotifications;
      Promise.resolve(readAllNotifications(readAllRequest)).then((res) => {
        if (res === null) {
          return;
        }
        if (res?.data?.Status == 1) {
          // Optionally update state or handle success
          fetchNotifications();
        }
      });
    });
    setData((prev) => prev.map((item) => ({ ...item, IsRead: true })));
    console.log("read1");
  };

  // ✅ Clear all notifications
  const handleClear = () => {
    // Assuming you want to clear all notifications, loop through data
    data.forEach((item) => {
      const clearRequest = {
        NotificationsID: item.NotificationsID,
        IsClear: true, // Set IsClear to true for each
        IsRead: true,
      };
      console.log(clearRequest);
      const clearNotifications = LearningService.UpdateRequestNotifications;
      Promise.resolve(clearNotifications(clearRequest)).then((res) => {
        if (res === null) {
          return;
        }
        if (res?.data?.Status == 1) {
          // Optionally update state or handle success
        }
      });
    });
    // Clear the data state after clearing
    setData([]);
  };

  // ✅ Count unread notifications for the badge
  const unreadCount = data.filter((item) => !item.IsRead).length;

  const notificationList = (
    <div className="nav-dropdown nav-notification" style={{ width: "40rem" }}>
      <div
        className="nav-notification-header d-flex justify-content-between align-items-center"
        style={{ padding: "8px 12px", borderBottom: "1px solid #f0f0f0" }}
      >
        <h4 className="mb-0" style={{ fontSize: "16px", fontWeight: 600 }}>
          Notifications
        </h4>

        {data.length > 0 && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "6px", // small space between buttons
            }}
          >
            <Button
              type="link"
              size="small"
              onClick={handleMarkAllRead}
              style={{
                padding: "0",
                height: "auto",
                fontSize: "13px",
                color: "#1890ff",
              }}
            >
              Mark all as read
            </Button>

            <Button
              type="link"
              size="small"
              onClick={handleClear}
              style={{
                padding: "0",
                height: "auto",
                fontSize: "13px",
                color: "#ff4d4f",
              }}
            >
              Clear
            </Button>
          </div>
        )}
      </div>

      <div
        className="nav-notification-body"
        style={{ maxHeight: "300px", overflowY: "auto" }}
      >
        {loading ? (
          <Spin size="small" />
        ) : (
          getNotificationBody(data, handleMarkRead)
        )}
      </div>

      {data.length > 0 && (
        <div
          className="nav-notification-footer"
          style={{
            textAlign: "center",
            padding: "8px",
            borderTop: "1px solid #f0f0f0",
          }}
        >
          <a className="d-block" href="#/">
            View all
          </a>
        </div>
      )}
    </div>
  );

  return (
    <Dropdown
      placement="bottomRight"
      overlay={notificationList}
      onVisibleChange={handleVisibleChange}
      visible={visible}
      trigger={["click"]}
    >
      <Menu mode="horizontal" disabledOverflow={true}>
        <Menu.Item key="notification">
          <Badge count={unreadCount}>
            <BellOutlined className="nav-icon mx-auto" />
          </Badge>
        </Menu.Item>
      </Menu>
    </Dropdown>
  );
};

export default NavNotification;
